import includes from 'lodash/includes';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { ServerApi } from './server-api';
import { Session } from './session';
import { BulkRequests, ServiceWorkerDynamicConfigService } from '@app/services/dynamic-configs/bulk-service-worker-config';
import { FleetCardsFeatureFlagService } from './feature-flags/fleet-cards';
import { GroupSelectionImprovementsConfigService } from './dynamic-configs/fe-group-selection-improvements-config';

import { IMoment, moment } from '../lib/moment';
import { fullName, extractResponseBody } from '../lib/utils';
import { deleteCacheEntry } from '@app/service-worker/dashboard/dashboard.sw.utils';
import { ELD_MODES } from '../lib/eld-modes';
import { ALL_ACCESSIBLE_GROUP_SCOPE, PERMISSIONS } from './../lib/permissions';

import { Role } from '../lib/roles.const';
import { CACHE_NAME } from '@app/service-worker/bulk/bulk.sw.consts';

export interface IResponseDriver {
  driver_company_id: string;
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  eld_mode: ELD_MODES;
  group_ids: number[];
  updated_at: string;
  deactivated_at: string;
}

export interface IDriver {
  id: number;
  driverCompanyId: string;
  name: string;
  firstName: string;
  lastName: string;
  value: number;
  groupIds: number[];
  eldMode: ELD_MODES;
  _sortFirstName: string;
  _sortLastName: string;
  deactivatedAt?: string;
}

export interface IListDriver {
  list: IDriver[];
}

export interface IDriverUser {
  user: IResponseDriver;
}

export interface IResponseDrivers {
  users: IDriverUser[];
}

@Injectable({ providedIn: 'root' })
export class DriversSearch {
  public _cachedList: IResponseDriver[] = [];
  public _lastUpdatedAt = null;
  private lastUpdatedAtWithBulkSW: IMoment = null;
  public _showAll: boolean = false;
  public _excludeNonCardHolders: boolean = false;
  public _cachedMappedList: Observable<IListDriver>;
  private hasBulkCacheBeenRefreshedInternal = false;

  public get hasBulkCacheBeenRefreshed(): boolean {
    return this.hasBulkCacheBeenRefreshedInternal;
  }

  constructor(
    private api: ServerApi,
    private session: Session,
    private fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
    private serviceWorkerDynamicConfigService: ServiceWorkerDynamicConfigService,
    private groupSelectionImprovementsConfigService: GroupSelectionImprovementsConfigService,
  ) {}

  public getList(
    preventGroupFiltering: boolean = false,
    excludeDeactivated: boolean = false,
    excludeNonEldDrivers: boolean = false,
    excludeMode: string = '',
    permissionToCheckGroupVisibility: PERMISSIONS = PERMISSIONS.NONE,
    showAll: boolean = false,
    excludeNonCardHolders: boolean = false,
    isTriggeredOnLoad: boolean = false,
  ): Observable<IListDriver> {
    this._showAll = showAll;
    this._excludeNonCardHolders = excludeNonCardHolders;
    return (this._cachedMappedList = this._serverRequest(isTriggeredOnLoad).pipe(
      map(
        this.mapList.bind(
          this,
          preventGroupFiltering,
          excludeDeactivated,
          excludeNonEldDrivers,
          excludeMode,
          permissionToCheckGroupVisibility,
        ),
      ),
    ));
  }

  public getByIds(ids) {
    return this._serverRequest().pipe(
      map((list: any) => {
        return list.filter(u => includes(ids, u.id));
      }),
    );
  }

  public async getDriverById(id: string, setDriver: (_: IDriver) => void) {

    if (!this._cachedList) return;

    // If cachedList array is empty then we fetch the driver through the cached mapped request which updates the cachchedList
    // Subsequent drivers are then searched from the cachedList array
    if (this._cachedList.length === 0) {
      if (!this._cachedMappedList) {
        this.getDriver(setDriver, id);
        return;
      }

      this._cachedMappedList.subscribe((driversList: IListDriver) => {
        setDriver(this.findDriver(id, driversList));
      });
    } else {
      const unmappedDriver: IResponseDriver = this._cachedList.find(
        (driver: IResponseDriver): boolean => driver.id.toString() === id,
      );
      const mappedDriver: IDriver = this.transformDriver(unmappedDriver);
      setDriver(mappedDriver);
    }
  }

  public clearBulkCache(): Promise<void> {
    deleteCacheEntry(CACHE_NAME, BulkRequests.DRIVER).then(() => this.hasBulkCacheBeenRefreshedInternal = true);
    return Promise.resolve();
  }

  private getDriver(setDriver: (_: IDriver) => void, id: string) {
    this.getList().subscribe((driversList: IListDriver): void => {
      setDriver(this.findDriver(id, driversList));
    });
  }

  private findDriver(id: string, driversList: IListDriver): IDriver {
    return driversList.list?.find(
      (driver: IDriver): boolean => driver.value?.toString() === id,
    );
  }

  private _getParams() {
    const params = new Map();
    if (this.fleetCardsFeatureFlagService.isCardBulkApiCardHoldersEnabled()) {
      params.set('card_holders', this._excludeNonCardHolders);
    }
    params.set('show_all', this._showAll);
    params.set('role', Role.DRIVER);
    params.set('group_ids', null); // Prevent group_ids param from being sent
    if (this._lastUpdatedAt) {
      params.set('updated_after', this._lastUpdatedAt.formatToServerUtc());
    }
    return params;
  }

  private _updateCachedList(data) {
    const newList = data.users;
    const cachedList = this._cachedList;
    newList?.forEach(u => {
      const driver = u.user;
      const index = cachedList.findIndex(_u => _u.id === driver.id);
      if (index >= 0) {
        cachedList.splice(index, 1, driver);
      } else {
        cachedList.push(driver);
      }
    });
    return cachedList;
  }

  private _serverRequest(isTriggeredOnLoad: boolean = false) {
    const params = this._getParams();
    if (isTriggeredOnLoad) params.set('on_app_load', true);

    const request$ = this.api.get('users/bulk', params).pipe(
      tap(() => {
        // lastUpdatedAt should be assigned on initial load so the next load only fetches updates
        if (this.groupSelectionImprovementsConfigService.setLastUpdatedAtOnLoadRequest() || !isTriggeredOnLoad) {
          this.setLastUpdatedAt();
        }
      }),
      map(extractResponseBody),
    );

    return request$.pipe(map(this._updateCachedList.bind(this)));
  }

  private setLastUpdatedAt(): void {
    this._lastUpdatedAt = moment();
  }

  public mapList(
    preventGroupFiltering: boolean,
    excludeDeactivated: boolean,
    excludeNonEldDrivers: boolean,
    excludeMode: string,
    permissionToCheckGroupVisibility: PERMISSIONS,
    list: IResponseDriver[] | IResponseDrivers,
  ): IListDriver {

    const filteredList: IResponseDriver[] = (
      list && 'users' in list ? list.users : Array.isArray(list) ? list : []
    ) as IResponseDriver[];
    const session = this.session;

    const sortedDrivers = filteredList
      .filter((resp: IResponseDriver | IDriverUser) => {
        const u: IResponseDriver = this.getDriverObject(resp);
        const groupFilter =
          preventGroupFiltering ||
          session.isInSelectedGroups(
            u.group_ids,
            permissionToCheckGroupVisibility || ALL_ACCESSIBLE_GROUP_SCOPE,
          );
        const deactivatedFilter = !(excludeDeactivated && u.deactivated_at);
        const nonEldFilter = !(excludeNonEldDrivers && u.eld_mode === ELD_MODES.None);
        const modeFilter = !(excludeMode && u.eld_mode === excludeMode);

        return groupFilter && deactivatedFilter && nonEldFilter && modeFilter;
      })
      .map((driver: IResponseDriver | IDriverUser) =>
        this.transformDriver(this.getDriverObject(driver)),
      )
      .sort(
        (a, b) =>
          a._sortFirstName.localeCompare(b._sortFirstName) ||
          a._sortLastName.localeCompare(b._sortLastName),
      );

    return { list: sortedDrivers };
  }

  private transformDriver(driver: IResponseDriver): IDriver {
    if (!driver) return;

    return {
      id: driver.id,
      name: fullName(driver, true),
      firstName: driver.first_name,
      lastName: driver.last_name,
      value: driver.id,
      driverCompanyId: driver.driver_company_id,
      groupIds: driver.group_ids || [],
      eldMode: driver.eld_mode,
      // Helper data for sorting
      _sortFirstName: driver.first_name.toLowerCase(),
      _sortLastName: driver.last_name.toLowerCase(),
      deactivatedAt: driver.deactivated_at,
    };
  }

  private getDriverObject(resp): IResponseDriver {
    return (
      'user' in resp ? resp.user : resp
    ) as IResponseDriver;
  }

  private transferDriverObject(data: IResponseDrivers): IResponseDriver[] {
    return data.users.map((userData: IDriverUser) => userData.user);
  }
}
